import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { deDE, enUS } from '@mui/material/locale';
import type { Localization } from '@mui/material/locale';
import './App.css';
import NavBar from './components/Layout/NavBar';
import UserContext from './store/User/UserContext';
import Loader from './components/Loaders/Loader';
import MessageContextProvider from './store/Message/MessageContextProvider';
import AlertNotification from './components/UI/Dialogs/AlertNotification';
import '@utils/dayjs';
import { SocketProvider } from './store/SocketContext';
import useAxiosInterceptor from './hooks/useAxiosInterceptor';
import useAuthenticatedUser from './hooks/useAuthenticatedUser';
import { TranslationErrorBoundary } from './components/ErrorBoundary';
import VersionUpdateNotification from './components/VersionUpdateNotification';

const MUI_LOCALES: Record<string, Localization> = { de: deDE, en: enUS };

const themeOptions = {
    palette: {
        primary: {
            main: '#0066FF',
        },
        secondary: {
            main: '#3f51b5',
        },
        info: {
            main: '#17183B',
        },
        error: {
            main: '#960200',
        },
        warning: {
            main: '#FFD046',
        },
        orange: {
            main: '#FFA500',
            contrastText: '#FFFFFF',
        },
    },
};

function App() {
    useAxiosInterceptor();
    const [loggedInUser, isUserLoading] = useAuthenticatedUser();
    const { i18n } = useTranslation();
    const language = i18n.resolvedLanguage ?? 'de';

    const theme = useMemo(() => createTheme(themeOptions, MUI_LOCALES[language] ?? deDE), [language]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <TranslationErrorBoundary>
                <VersionUpdateNotification />
                {isUserLoading ? (
                    <Loader />
                ) : (
                    <MessageContextProvider>
                        <SocketProvider>
                            <UserContext.Provider value={loggedInUser}>
                                <NavBar />
                                <AlertNotification
                                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                                    onClose={undefined}
                                />
                            </UserContext.Provider>
                        </SocketProvider>
                    </MessageContextProvider>
                )}
            </TranslationErrorBoundary>
        </ThemeProvider>
    );
}

export default App;
